/* eslint-disable react/jsx-no-comment-textnodes */
import type { ReactNode, ReactElement } from 'react';

import { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  List,
  Badge,
  Divider,
  ListItem,
  Typography,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
// @ts-ignore legacy boundary
import { Task, ArrowRightAlt } from '@mui/icons-material';
import Scrollbar from '../../components/Scrollbar';
import MenuPopover from '../../components/MenuPopover';
import { MIconButton } from '../../components/@material-extend';
import usePrereqs from 'src/hooks/usePrereqs';
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { skipPhoneVerification } from 'src/backend/user';

export interface PrereqItemProps {
  prereq?: Record<string, any>;
  onSkip?: (...args: any[]) => any;
  [key: string]: any;
}

function PrereqItem({ prereq, onSkip, t }: PrereqItemProps) {
  return (
    <ListItem
      disablePadding
      sx={{
        '&:not(:last-of-type)': { mb: '1px' },
        ...(prereq.isUnRead && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemButton
        {...(prereq.location
          ? {
              to: prereq.location,
              component: RouterLink,
            }
          : {
              onClick: prereq.action,
            })}
        sx={{
          py: 1.5,
          px: 2.5,
        }}
      >
        <ListItemText
          primary={
            <Typography variant="subtitle2">{prereq.displayName}</Typography>
          }
          secondary={
            <Typography variant="body2">{prereq.description}</Typography>
          }
        />
        <ListItemAvatar sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {prereq.skippable && onSkip && (
            <Button
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSkip();
              }}
              sx={{
                minWidth: 'auto',
                fontSize: '12px',
                color: 'text.secondary',
                textTransform: 'none',
                borderRadius: 1,
                px: 1,
                '&:hover': {
                  color: 'error.main',
                  backgroundColor: 'error.lighter',
                },
              }}
            >
              {t('common.skip')}
            </Button>
          )}
          <ArrowRightAlt color="primary" />
        </ListItemAvatar>
      </ListItemButton>
    </ListItem>
  );
}

export default function PrereqsPopover() {
  const { t } = useTranslation('translation');
  const { invalidateUserData } = useAuth();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const prereqs = usePrereqs();

  const handleSkip = async (prereq) => {
    if (prereq.skipKey) {
      localStorage.setItem(prereq.skipKey, '1');
    } else {
      await skipPhoneVerification();
    }
    await invalidateUserData();
  };

  if (prereqs.length === 0) {
    return null;
  }

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={() => setOpen(true)}
        color={open ? 'primary' : 'default'}
      >
        <Badge badgeContent={prereqs.length} color="error">
          <Task />
        </Badge>
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        sx={{ width: 360 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">
              {t('finish-profile.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('finish-profile.pending-tasks', { count: prereqs.length })}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List disablePadding>
            {prereqs.map((prereq, i) => (
              <PrereqItem
                key={i}
                prereq={prereq}
                t={t}
                onSkip={prereq.skippable ? () => handleSkip(prereq) : undefined}
              />
            ))}
          </List>
        </Scrollbar>
      </MenuPopover>
    </>
  );
}
